import { cn } from '@/lib/utils';
// Type-only: the underline and font-size controls below are optional and only
// render when their extension is loaded, but their commands reach this file
// through module augmentation. Apps that compile this component without
// loading those extensions (chiroup-admin) need the declarations in scope to
// typecheck. `import type {}` pulls in the augmentation and no runtime code.
import type {} from '@chiroup/core/functions/memoFontSize';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import type {} from '@tiptap/extension-underline';
import { Transaction } from '@tiptap/pm/state';
import {
  BubbleMenu,
  EditorProvider,
  useCurrentEditor,
  type Editor,
  type JSONContent,
} from '@tiptap/react';
import { ChevronDown, ExternalLink, Pencil, Unlink } from 'lucide-react';
import { useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { Icons } from '../icons';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';
import EditorIcon from './EditorIcon';
import { FieldErrors } from './FieldErrors';

type EditorStuffProps = {
  ref: any;
};

const EditorStuff = ({ ref }: EditorStuffProps) => {
  const { editor } = useCurrentEditor();

  const editorRef: React.MutableRefObject<Editor | null> = useRef(null);

  useImperativeHandle(ref, () => ({
    clearContent: () => {
      editorRef.current?.commands.clearContent();
    },
    focus: () => {
      editorRef.current?.commands.focus();
    },
    setContent: (content: string) => {
      editorRef.current?.commands.setContent(content);
    },
    getContent: () => {
      return editorRef.current?.getHTML();
    },
    insertContent: (content: string) => {
      editorRef.current?.commands.insertContent(content);
    },
  }));
  if (!editor) return null;
  editorRef.current = editor;

  return null;
};

const EditableUpdater = ({ disabled }: { disabled: boolean }) => {
  const { editor } = useCurrentEditor();

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  return null;
};

const LinkBubbleMenu = () => {
  const { editor } = useCurrentEditor();

  const handleOpenLink = useCallback(() => {
    if (!editor) return;
    const href = editor.getAttributes('link').href;

    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    let url = window.prompt('Enter a URL to link to', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  const href = editor.getAttributes('link').href;

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100, placement: 'bottom-start' }}
      shouldShow={({ editor: ed }) => ed.isActive('link')}
    >
      <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-lg dark:border-dark-gray-600 dark:bg-dark-gray-800">
        <span
          className="max-w-[200px] truncate px-2 text-sm text-gray-600 dark:text-dark-gray-300"
          title={href}
        >
          {href}
        </span>
        <button
          type="button"
          onClick={handleOpenLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-200"
          title="Open link"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleEditLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-200"
          title="Edit link"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleRemoveLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-red-400"
          title="Remove link"
        >
          <Unlink className="h-4 w-4" />
        </button>
      </div>
    </BubbleMenu>
  );
};

/**
 * Font size picker.
 *
 * Deliberately not a native `<select>`: at toolbar size the browser paints its
 * own chevron over the value and clips it, and the appearance resets needed to
 * suppress that behave differently per engine. The team chat composer uses
 * plain buttons for the same reason, so this follows that pattern.
 *
 * The list is a Popover rather than an absolutely positioned element because
 * the toolbar sits inside a ScrollArea viewport, which clips its overflow —
 * a plain dropdown renders but is invisible. Popover portals to the body.
 */
const FontSizeMenu = ({
  editor,
  fontSizes,
  defaultFontSize,
}: {
  editor: Editor;
  fontSizes: number[];
  defaultFontSize: number;
}) => {
  const [open, setOpen] = useState(false);

  const activeSize = editor.getAttributes('textStyle').fontSize as string | undefined;
  // Unstyled text reports the size it actually renders at rather than a
  // placeholder, so the control always reflects the cursor.
  const current = activeSize ? parseInt(activeSize, 10) : defaultFontSize;

  const choose = (size: number) => {
    if (size === defaultFontSize) {
      // Clear the mark rather than writing a span restating the default.
      editor.chain().focus().unsetFontSize().run();
    } else {
      editor.chain().focus().setFontSize(`${size}px`).run();
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Font size"
          aria-label="Font size"
          // Matches EditorIcon's box — p-1 around 24px of content. align-bottom
          // because the sibling buttons hold SVGs, whose inline baseline is
          // their bottom edge; text's baseline sits higher, so baseline
          // alignment would drop this box below the icon row.
          className="inline-flex cursor-pointer rounded-md p-1 align-bottom text-gray-900 hover:bg-gray-50 dark:text-dark-gray-200 dark:hover:bg-dark-gray-600"
        >
          <span className="flex h-6 items-center gap-0.5">
            <span className="text-base font-medium tabular-nums leading-none">{current}</span>
            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.25} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-16 p-1">
        <span role="listbox" aria-label="Font size" className="flex flex-col">
          {fontSizes.map(size => (
            <button
              key={size}
              type="button"
              role="option"
              aria-selected={size === current}
              onClick={() => choose(size)}
              className={cn(
                'rounded px-2 py-1 text-left text-sm hover:bg-gray-100 dark:hover:bg-dark-gray-700',
                size === current
                  ? 'font-semibold text-primary-600 dark:text-primary-400'
                  : 'text-gray-900 dark:text-dark-gray-200'
              )}
            >
              {size}
            </button>
          ))}
        </span>
      </PopoverContent>
    </Popover>
  );
};

type MenuBarProps = {
  extraButtons?: {
    icon: Icons;
    type: 'setContent';
    fn: () => any;
  }[];
  ref: any;
  className?: string;
  fontSizes?: number[];
  defaultFontSize?: number;
  toolbarExtra?: React.ReactNode;
};

/** Extensions are opt-in per editor, so a control is only shown when the
 *  extension backing it is actually loaded. Editors that don't load it (chat,
 *  reports, clinical skills) render exactly the toolbar they always have. */
const hasExtension = (editor: Editor, name: string) =>
  editor.extensionManager.extensions.some(extension => extension.name === name);

const MenuBar = ({
  ref,
  extraButtons,
  className,
  fontSizes,
  defaultFontSize,
  toolbarExtra,
}: MenuBarProps) => {
  const { editor } = useCurrentEditor();

  const setLink = useCallback(() => {
    if (!editor) return;
    const isLinked = editor.isActive('link');

    if (isLinked) {
      editor.chain().focus().unsetLink().run();
    } else {
      const previousUrl = editor.getAttributes('link').href;
      let url = window.prompt('Enter a URL to link to', previousUrl);

      if (url === null) {
        return;
      }

      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();

        return;
      }

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const clearFormatting = useCallback(() => {
    if (!editor) return;
    const chain = editor
      .chain()
      .focus('all')
      .selectAll()
      .clearNodes()
      .unsetBold()
      .unsetItalic()
      .unsetStrike();

    if (hasExtension(editor, 'underline')) {
      chain.unsetUnderline();
    }

    if (hasExtension(editor, 'memoFontSize')) {
      chain.unsetFontSize();
    }

    chain.focus(1).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <ScrollArea>
      <div
        className={cn(
          'flex flex-row flex-nowrap gap-1 border border-b-0 min-w-full border-gray-300 rounded-t-md transition duration-150 ease-in-out sm:leading-5 dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 outline-none p-2 h-12 bg-gray-50 justify-between',
          className
        )}
      >
        <div>
          <EditorIcon
            icon={Icons.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          />
          <EditorIcon
            icon={Icons.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          />
          {hasExtension(editor, 'underline') && (
            <EditorIcon
              icon={Icons.underline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={!editor.can().chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
            />
          )}
          <EditorIcon
            icon={Icons.strikethrough}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
          />
          {!!fontSizes?.length && hasExtension(editor, 'memoFontSize') && (
            <FontSizeMenu
              editor={editor}
              fontSizes={fontSizes}
              defaultFontSize={defaultFontSize ?? fontSizes[0]}
            />
          )}
          <EditorIcon icon={Icons.link} isActive={editor.isActive('link')} onClick={setLink} />
          <EditorIcon
            icon={Icons.unorderedList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
          />
          <EditorIcon
            icon={Icons.orderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
          />
          <EditorIcon
            icon={Icons.blockQuote}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
          />
          <EditorIcon
            icon={Icons.columnsHorizontal}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          />
          <EditorIcon
            icon={Icons.undo}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          />
          <EditorIcon
            icon={Icons.redo}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          />
          <EditorIcon icon={Icons.eraser} onClick={clearFormatting} />
        </div>
        <div className="flex flex-row items-center gap-1">
          {toolbarExtra}
          {extraButtons?.map((button, index) => (
            <EditorIcon
              key={`editor-btn-${button.icon}-${index}`}
              icon={button.icon}
              onClick={async () => {
                const res = await button.fn?.();

                if (button.type === 'setContent') {
                  editor.commands.setContent(res || '');
                }
              }}
            />
          ))}
        </div>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};

type Props = {
  ref?: any;
  value?: JSONContent | string;
  name?: string;
  label?: string;
  onChange?: (val?: any) => any;
  className?: string;
  containerClassName?: string;
  small?: boolean;
  toolbar?: boolean;
  html?: boolean;
  text?: boolean;
  disabled?: boolean;
  extraButtons?: {
    icon: Icons;
    type: 'setContent';
    fn: () => any;
  }[];
  errors?: FormError;
  labelColor?: boolean;
  extensions: any[];
  rightSide?: React.ReactNode;
  /** Rendered under the editor, inside the provider — so it can read the
   *  editor via useCurrentEditor (e.g. a character counter). */
  footer?: React.ReactNode;
  /** Shows the font-size control when the memoFontSize extension is loaded. */
  fontSizes?: number[];
  /** Size unstyled text renders at; shown when no explicit size is applied. */
  defaultFontSize?: number;
  /** Rendered at the right end of the toolbar, inside the editor provider, so
   *  it can reach the editor through `useCurrentEditor`. */
  toolbarExtra?: React.ReactNode;
};

export const TiptapEditorBase = ({
  ref,
  value,
  name,
  label,
  className = 'border block w-full border-gray-300 rounded-b-md transition duration-150 ease-in-out sm:leading-5 dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 outline-none p-2',
  containerClassName,
  onChange,
  small,
  toolbar = true,
  html = false,
  text = false,
  disabled = false,
  extraButtons,
  errors,
  labelColor = false,
  extensions = [],
  rightSide,
  footer,
  fontSizes,
  defaultFontSize,
  toolbarExtra,
}: Props) => {
  const id = useId();

  const debouncedOnUpdate = ({
    editor,
    transaction,
  }: {
    editor: any;
    transaction: Transaction;
  }) => {
    // Skip non-content events (e.g. setEditable) that fire onUpdate
    // without actually changing the document.
    if (!transaction.docChanged) return;
    const val = text ? editor.getText() : html ? editor.getHTML() : editor.getJSON();
    onChange?.(val);
  };

  return (
    <div
      className={cn(
        'tiptap prose flex flex-col w-full max-w-none! bg-white dark:bg-dark-gray-900',
        small ? 'tiptap-small' : '',
        containerClassName ?? ''
      )}
    >
      <div className={cn('flex flex-row justify-between items-bottom', rightSide ? 'pb-2' : '')}>
        {label && (
          <label
            className={cn(
              `block text-sm font-medium leading-5 sm:mt-px`,
              labelColor
                ? 'text-primary-600 dark:text-dark-gray-200'
                : 'text-gray-900  dark:text-dark-gray-200',
              rightSide ? 'sm:pt-4' : 'sm:pt-2'
            )}
            htmlFor={name || id}
          >
            {label}
          </label>
        )}
        {rightSide && (
          <div className={cn(!!disabled && 'opacity-50', 'flex flex-row justify-end')}>
            {rightSide}
          </div>
        )}
      </div>
      <EditorProvider
        content={value}
        extensions={extensions}
        onUpdate={debouncedOnUpdate}
        editable={!disabled}
        editorProps={{
          attributes: {
            class: cn(
              className,
              toolbar ? '' : 'rounded-t-md',
              disabled
                ? 'opacity-50 bg-gray-100 dark:bg-dark-gray-700 pointer-events-none'
                : 'pointer-events-auto'
            ),
            disabled: `${disabled}`,
            name: name || id,
          },
        }}
        slotBefore={
          toolbar ? (
            <MenuBar
              extraButtons={extraButtons}
              ref={ref}
              className={disabled ? 'opacity-50' : undefined}
              fontSizes={fontSizes}
              defaultFontSize={defaultFontSize}
              toolbarExtra={toolbarExtra}
            />
          ) : null
        }
      >
        <EditorStuff ref={ref} />
        <EditableUpdater disabled={disabled} />
        <LinkBubbleMenu />
        {footer}
      </EditorProvider>
      <FieldErrors errors={errors} />
    </div>
  );
};
